import { computed, inject, Injectable, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import _ from 'lodash';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import {
  DyeingForecastFormGroup,
  DyeingGroupSummary,
  FabricExtraPercentageFormGroup,
  FabricSummary,
} from '@app/models/production/dyeing/dyeing-forecast-forms';
import { DyeingGroup } from '@app/models/production/production-card-forms';
import { ProductionCardStatusUtil } from '@app/utilities/production-card-status.util';
import { Asset } from '../models/asset/asset';
import { WorkOrder, WorkOrderFabric } from '../models/order/work-order';
import { DyeingColor } from '../models/production/dyeing/dyeing-color';
import { DyeingForecast } from '../models/production/dyeing/dyeing-forecast';
import { DyeingForecastProxyService } from '../proxies/production/dyeing-forecast-proxy.service';
import { ProductionCardService } from './production-card.service';

@Injectable({
  providedIn: 'root',
})
export class DyeingForecastService {
  public workOrder!: WorkOrder;
  public colors: DyeingColor[] = [];
  public dyeingAssets: Asset[] = [];
  public savedDyeingForecasts: DyeingForecast[] = [];
  public readonly DYEING_AND_FINISHING_LOSS_PERCENTAGE = 6;

  public dyeingForecastsFormArray: FormArray<FormGroup<DyeingForecastFormGroup>> = new FormArray<FormGroup<DyeingForecastFormGroup>>([]);

  private _pending = signal(0);
  readonly isLoading = computed(() => this._pending() > 0);

  private readonly dyeingForecastProxyService = inject(DyeingForecastProxyService);
  private readonly productionCardService = inject(ProductionCardService);

  public initialize(workOrder: WorkOrder, colors: DyeingColor[], dyeingAssets: Asset[]) {
    this.workOrder = workOrder;
    this.colors = colors;
    this.dyeingAssets = dyeingAssets;
    this.loadAllForecasts();
  }

  private loadAllForecasts() {
    this._pending.update((v) => v + 1);
    this.dyeingForecastProxyService
      .search(
        {
          workOrderReference: this.workOrder!.id,
        },
        true,
      )
      .subscribe({
        next: ({ data }) => {
          this._pending.update((v) => v - 1);
          this.dyeingForecastsFormArray.clear();
          this.savedDyeingForecasts = data!;
          data!.forEach((dyeingForecast) => {
            const formGroup = this.createDyeingForecastFormGroup(dyeingForecast);
            this.dyeingForecastsFormArray.push(formGroup);
          });
        },
        error: () => {
          this._pending.update((v) => v - 1);
        },
      });
  }

  public get dyeingForecasts(): FormArray<FormGroup<DyeingForecastFormGroup>> {
    return this.dyeingForecastsFormArray;
  }

  public get dyeingGroupsSummary(): DyeingGroupSummary[] {
    //get the groups and them add all the existing forecasts for the fabrics
    const dyeingGroups = this.dyeingGroups;
    const dyeingGroupsSummary: DyeingGroupSummary[] = [];
    dyeingGroups.forEach((dyeingGroup) => {
      const fabrics = dyeingGroup.fabrics;
      const color = dyeingGroup.color;
      const fabricSummaries: FabricSummary[] = fabrics.map((fabric) => {
        // 1. Get all the forecasts for the color and fabric. there can be multiple forecasts for the same fabric and color
        const allForecasts = this.dyeingForecasts.controls.filter((formGroup) => {
          return formGroup.controls.color.value?.id === color.id && formGroup.controls.created.value === false;
        });

        const productionCardsFormArray = this.productionCardService.getProductionCardEntriesByColor(color);

        // const allProductionCards = this.productionCardService.

        // 2. Add up all the quantities for the fabric
        let totalPlannedQuantity = allForecasts.reduce((acc, formGroup) => {
          const fabricExtraPercentage = formGroup.controls.fabricExtraPercentages.value.find(
            (f) => f.fabricCode === fabric.fabricCode,
          )?.percentage;
          const percentage = this.getFabricWeightedPercentage(formGroup, fabric.fabricCode);
          let quantity = formGroup.controls.quantityInLbsPerProductionCard.value * percentage;
          if (fabricExtraPercentage) {
            quantity += (quantity * fabricExtraPercentage) / 100;
          }
          quantity *= formGroup.controls.productionCardCount.value;
          return acc + quantity;
        }, 0);

        // 3. Add up all the production cards for the fabric, excluding voided ones (deleted/rejected)
        productionCardsFormArray.controls.forEach((formGroup) => {
          const status = formGroup.controls.status.value;
          if (!_.isNil(status) && ProductionCardStatusUtil.voidedStatuses.includes(status)) {
            return;
          }
          formGroup.controls.productionCardEntries.controls.forEach((entry) => {
            if (entry.controls.fabricCode.value === fabric.fabricCode && !_.isNil(entry.controls.quantity.value)) {
              totalPlannedQuantity += entry.controls.quantity.value;
            }
          });
        });

        const requiredQuantity = fabric.garmetDetails?.find((gd) => gd.colorReference === color.id)?.quantityInPounds ?? 0;
        const dyeingAndFinishingLossPercentage =
          typeof fabric.fabric?.finishingDetails?.dyeingAndFinishingLossPercentage === 'number' &&
          fabric.fabric.finishingDetails.dyeingAndFinishingLossPercentage > 0
            ? fabric.fabric.finishingDetails.dyeingAndFinishingLossPercentage
            : this.DYEING_AND_FINISHING_LOSS_PERCENTAGE;
        const knittingLossPercentage = fabric.fabric?.greige?.knittingProcessLossPercentage ?? 0;

        // Apply both loss percentages like Work Order Summary Viewer
        const knittingQuantity = requiredQuantity / (1 - dyeingAndFinishingLossPercentage / 100);
        const fabricQuantityRequiredInUOM = knittingQuantity / (1 - knittingLossPercentage / 100);

        return {
          fabricCode: fabric.fabricCode,
          fabric: fabric.fabric,
          quantityRequiredInUOM: fabricQuantityRequiredInUOM,
          quantityPlannedInUOM: totalPlannedQuantity,
        };
      });
      dyeingGroupsSummary.push({
        color: color,
        fabrics: fabricSummaries,
      });
    });
    return dyeingGroupsSummary;
  }

  public get dyeingGroups(): DyeingGroup[] {
    //1. Group by color reference that we can find in garmetDetails
    const groupRecord: Record<string, WorkOrderFabric[]> = {};
    this.workOrder?.fabrics.forEach((fabric) => {
      fabric.garmetDetails?.forEach((garmetDetail) => {
        if (!groupRecord[garmetDetail.colorReference]) {
          groupRecord[garmetDetail.colorReference] = [];
        }
        groupRecord[garmetDetail.colorReference].push(fabric);
      });
    });

    //2. Map to DyeingGroup
    const dyeingGroups: DyeingGroup[] = [];
    Object.keys(groupRecord).forEach((colorReference) => {
      const fabrics = groupRecord[colorReference];
      const color = this.colors.find((c) => c.id === colorReference);
      if (color) {
        dyeingGroups.push({ fabrics, color });
      }
    });
    return dyeingGroups;
  }

  public addNewForecast() {
    const formGroup = this.createDyeingForecastFormGroup();
    this.dyeingForecastsFormArray.push(formGroup);
  }

  public allColors(): DyeingColor[] {
    //return the colors that are in the work order fabrics
    return this.dyeingGroups.map((dg) => dg.color);
  }

  public allAssets(): Asset[] {
    return this.dyeingAssets;
  }

  // #region Actions
  public saveForecast(formGroup: FormGroup<DyeingForecastFormGroup>, complete = false) {
    const dyeingForecast: DyeingForecast = {
      id: formGroup.controls.id.value ?? undefined,
      workOrderReference: this.workOrder.id,
      colorReference: formGroup.controls.color.value!.id,
      dyeingAssetId: formGroup.controls.dyeingAsset.value!.assetId,
      quantityInLbsPerProductionCard: formGroup.controls.quantityInLbsPerProductionCard.value,
      totalQuantityInLbs: this.getTotalAfterExtra(formGroup) ?? 0,
      productionCardCount: formGroup.controls.productionCardCount.value,
      fabricExtraPercentages: formGroup.controls.fabricExtraPercentages.value.map((fabric) => ({
        fabricCode: fabric.fabricCode!,
        percentage: fabric.percentage!,
      })),
      created: formGroup.controls.created.value,
    };
    const saveForecast$ = dyeingForecast.id
      ? this.dyeingForecastProxyService.update(dyeingForecast)
      : this.dyeingForecastProxyService.create(dyeingForecast);

    saveForecast$.subscribe({
      next: (response) => {
        if (!_.isNil(response.data)) {
          if (complete) {
            this.dyeingForecastProxyService.complete(response.data.id!).subscribe({
              next: (completeResponse) => {
                if (completeResponse && completeResponse.data) {
                  formGroup.controls.created.setValue(completeResponse.data.created);
                  this.productionCardService.getProductionCardsForDyeingForecast(completeResponse.data.id!);
                  GlobalMessageUtil.success('Success', 'Dyeing forecast completed successfully');
                }
              },
            });
          } else {
            GlobalMessageUtil.success('Success', 'Dyeing forecast saved successfully');
          }
        }
      },
    });
  }
  // #endregion

  private createDyeingForecastFormGroup(dyeingForecast?: DyeingForecast) {
    const notNew = !_.isNil(dyeingForecast);
    const fabricExtraPercentages = notNew
      ? dyeingForecast.fabricExtraPercentages.length > 0
        ? dyeingForecast.fabricExtraPercentages.map((fabric) => {
            return new FormGroup<FabricExtraPercentageFormGroup>({
              fabricCode: new FormControl(fabric.fabricCode, { validators: [Validators.required], nonNullable: true }),
              percentage: new FormControl(fabric.percentage, {
                validators: [Validators.required],
                nonNullable: true,
              }),
            });
          })
        : this.workOrder.fabrics.map((fabric) => {
            return new FormGroup<FabricExtraPercentageFormGroup>({
              fabricCode: new FormControl(fabric.fabricCode, { validators: [Validators.required], nonNullable: true }),
              percentage: new FormControl(0, { validators: [Validators.required], nonNullable: true }),
            });
          })
      : [];

    const color: DyeingColor | undefined = this.colors.find((c) => c.id === dyeingForecast?.colorReference);

    if (_.isNil(color) && notNew) {
      throw new Error('Color not found');
    }

    if (color && fabricExtraPercentages.length > 0) {
      const group = this.dyeingGroups.find((dg) => dg.color.id === color.id);
      if (group && group.fabrics.length !== fabricExtraPercentages.length) {
        //add the missing fabrics
        const missingFabrics = group.fabrics.filter((fabric) => {
          return !fabricExtraPercentages.some((formGroup) => formGroup.get('fabricCode')?.value === fabric.fabricCode);
        });
        missingFabrics.forEach((fabric) => {
          fabricExtraPercentages.push(
            new FormGroup<FabricExtraPercentageFormGroup>({
              fabricCode: new FormControl(fabric.fabricCode, { validators: [Validators.required], nonNullable: true }),
              percentage: new FormControl(0, { validators: [Validators.required], nonNullable: true }),
            }),
          );
        });
      }
    }

    const dyeingAsset: Asset | undefined = this.dyeingAssets.find((a) => a.assetId === dyeingForecast?.dyeingAssetId);
    if (_.isNil(dyeingAsset) && notNew) {
      console.warn('Dyeing asset not found');
    }
    const formGroup = new FormGroup<DyeingForecastFormGroup>({
      color: new FormControl<DyeingColor | null>(color ?? null, { validators: [Validators.required] }),
      id: new FormControl<string | null>(dyeingForecast?.id ?? null, {}),
      dyeingAsset: new FormControl<Asset | null>(dyeingAsset ?? null, { validators: [Validators.required] }),
      quantityInLbsPerProductionCard: new FormControl<number>(dyeingForecast?.quantityInLbsPerProductionCard ?? 0, {
        validators: [Validators.required, Validators.min(0.01)],
        nonNullable: true,
      }),
      quantityInLbsPerProductionCardAfterExtra: new FormControl<number | null>({
        value: dyeingForecast?.quantityInLbsPerProductionCardAfterExtra
          ? _.round(dyeingForecast?.quantityInLbsPerProductionCardAfterExtra, 2)
          : null,
        disabled: true,
      }),
      productionCardCount: new FormControl<number>(dyeingForecast?.productionCardCount ?? 0, {
        validators: [Validators.required, Validators.min(1)],
        nonNullable: true,
      }),
      fabricExtraPercentages: new FormArray<FormGroup<FabricExtraPercentageFormGroup>>(fabricExtraPercentages),
      created: new FormControl<boolean>(dyeingForecast?.created ?? false, { nonNullable: true }),
    });

    formGroup.controls.color.valueChanges.subscribe((color) => {
      if (!_.isNil(color?.id)) {
        const newFabricExtraPercentages =
          this.dyeingGroups
            .find((dg) => dg.color.id === color.id)
            ?.fabrics?.map((fabric) => {
              return new FormGroup<FabricExtraPercentageFormGroup>({
                fabricCode: new FormControl(fabric.fabricCode, { validators: [Validators.required], nonNullable: true }),
                percentage: new FormControl(0, { validators: [Validators.required], nonNullable: true }),
              });
            }) || [];
        formGroup.setControl(
          'fabricExtraPercentages',
          new FormArray<FormGroup<FabricExtraPercentageFormGroup>>(newFabricExtraPercentages),
          { emitEvent: false },
        );
      }
    });

    formGroup.controls.quantityInLbsPerProductionCard.valueChanges.subscribe(() => {
      const total = this.getTotalAfterExtra(formGroup);
      formGroup.controls.quantityInLbsPerProductionCardAfterExtra.setValue(total, { emitEvent: false });
    });

    formGroup.controls.fabricExtraPercentages.valueChanges.subscribe(() => {
      const total = this.getTotalAfterExtra(formGroup);
      formGroup.controls.quantityInLbsPerProductionCardAfterExtra.setValue(total, { emitEvent: false });
    });

    return formGroup;
  }

  public getFabricWeightedPercentage(formGroup: FormGroup<DyeingForecastFormGroup>, fabricCode: string): number {
    const group = this.dyeingGroups.find((dg) => dg.color.id === formGroup.controls.color.value?.id);
    if (group) {
      // add up all the fabric.quantity
      const total = group.fabrics.reduce((acc, fabric) => {
        const foundGarmet = fabric.garmetDetails?.find((g) => g.colorReference === group.color.id);
        if (foundGarmet) {
          return acc + foundGarmet.quantity;
        } else {
          return acc; // Si no se encuentra, suma 0 (o lo que tenga sentido en tu contexto)
        }
      }, 0);
      const fabric = group.fabrics.find((f) => f.fabricCode === fabricCode);
      if (fabric) {
        //round to 2 decimal places
        const percentage = fabric.garmetDetails?.find((g) => g.colorReference === group.color.id)?.quantity;
        const percentageTotal = ((percentage! / total) * 100) / 100;

        return percentageTotal;
      }
    }
    return 0;
  }

  private getTotalAfterExtra(formGroup: FormGroup<DyeingForecastFormGroup>) {
    const fabricTotal: Record<string, number> = {};
    const group = this.dyeingGroups.find((dg) => dg.color.id === formGroup.controls.color.value?.id);
    if (group) {
      group.fabrics.forEach((fabric) => {
        fabricTotal[fabric.fabricCode] =
          this.getFabricWeightedPercentage(formGroup, fabric.fabricCode) * formGroup.controls.quantityInLbsPerProductionCard.value;
      });
      formGroup.controls.fabricExtraPercentages.controls.forEach((fabricExtraPercentage) => {
        const fabricCode = fabricExtraPercentage.value.fabricCode!;
        const percentage = fabricExtraPercentage.value.percentage!;
        if (percentage > 0) {
          fabricTotal[fabricCode] += (fabricTotal[fabricCode] * percentage) / 100;
        }
      });
    }
    const total = Object.values(fabricTotal).reduce((acc, value) => acc + value, 0);
    if (total > 0) {
      return Math.round(total * 100) / 100;
    } else {
      return 0;
    }
  }

  public getFabric(fabricCode: string): WorkOrderFabric | null {
    return this.workOrder.fabrics.find((fabric) => fabric.fabricCode === fabricCode) ?? null;
  }
}
